/**
 * Unified Currency Types
 *
 * Centralized currency definitions for financial operations.
 * Supports multiple currencies with conversion capabilities.
 */

/**
 * Core currency interface
 */
export interface Currency {
  code: string; // ISO 4217 code (USD, EUR, PLN)
  name: string; // Full name
  symbol: string; // Currency symbol ($, €, zł)
  decimal_places: number;
  is_active: boolean;
}

/**
 * Currency code type
 */
export type CurrencyCode = 'PLN' | 'EUR' | 'USD' | 'GBP' | 'CHF' | 'CZK' | 'SEK' | 'NOK' | 'DKK';

/**
 * Supported currencies
 */
export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'PLN', name: 'Polski Złoty', symbol: 'zł', decimal_places: 2, is_active: true },
  { code: 'EUR', name: 'Euro', symbol: '€', decimal_places: 2, is_active: true },
  { code: 'USD', name: 'US Dollar', symbol: '$', decimal_places: 2, is_active: true },
  { code: 'GBP', name: 'British Pound', symbol: '£', decimal_places: 2, is_active: true },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimal_places: 2, is_active: true },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', decimal_places: 2, is_active: true },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimal_places: 2, is_active: true },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimal_places: 2, is_active: true },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimal_places: 2, is_active: true },
];

/**
 * Currency exchange rate
 */
export interface ExchangeRate {
  from_currency: CurrencyCode;
  to_currency: CurrencyCode;
  rate: number;
  date: string;
  source?: string;
}

/**
 * Money amount with currency
 */
export interface Money {
  amount: number;
  currency: CurrencyCode;
}

/**
 * Price with optional discount
 */
export interface Price extends Money {
  discount_amount?: number;
  discount_percent?: number;
  tax_rate?: number;
  tax_included?: boolean;
}

/**
 * Get currency by code
 */
export function getCurrencyByCode(code: string): Currency | undefined {
  return SUPPORTED_CURRENCIES.find((c) => c.code === code.toUpperCase());
}

/**
 * Format money amount with currency symbol
 */
export function formatMoney(money: Money, locale = 'pl-PL'): string {
  const currency = getCurrencyByCode(money.currency);
  if (!currency) {
    return `${money.amount.toFixed(2)} ${money.currency}`;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: currency.decimal_places,
    maximumFractionDigits: currency.decimal_places,
  }).format(money.amount);
}

/**
 * Format amount with currency (shorthand)
 */
export function formatAmount(
  amount: number,
  currencyCode: CurrencyCode = 'PLN',
  locale = 'pl-PL'
): string {
  return formatMoney({ amount, currency: currencyCode }, locale);
}

/**
 * Convert money between currencies
 */
export function convertMoney(money: Money, toCurrency: CurrencyCode, exchangeRate: number): Money {
  return {
    amount: money.amount * exchangeRate,
    currency: toCurrency,
  };
}

/**
 * Calculate gross from net amount
 */
export function calculateGross(netAmount: number, taxRate: number): number {
  return netAmount * (1 + taxRate / 100);
}

/**
 * Calculate net from gross amount
 */
export function calculateNet(grossAmount: number, taxRate: number): number {
  return grossAmount / (1 + taxRate / 100);
}

/**
 * Sum multiple money amounts (must be same currency)
 */
export function sumMoney(amounts: Money[]): Money {
  if (amounts.length === 0) {
    return { amount: 0, currency: 'PLN' };
  }

  const currency = amounts[0].currency;
  const allSameCurrency = amounts.every((a) => a.currency === currency);

  if (!allSameCurrency) {
    throw new Error('Cannot sum amounts with different currencies');
  }

  const total = amounts.reduce((sum, a) => sum + a.amount, 0);
  return { amount: total, currency };
}

/**
 * Default currency for new organizations
 */
export const DEFAULT_CURRENCY: CurrencyCode = 'PLN';

// ─── Reguła kraj → waluta ────────────────────────────────────────────────────
//
// Cennik kanoniczny (docs/09-STRATEGIA-BIZNES/05-CENNIK-KANONICZNY.md) prowadzi
// ceny w trzech walutach: PLN (kanon), EUR i USD — to osobne cenniki wartościowe,
// nie przeliczenia kursowe. Klient płaci w walucie swojego kraju: Polska w PLN,
// strefa euro w EUR, USA w USD; kraje spoza tej trójki dostają cennik EUR, bo
// innych cenników nie prowadzimy. Ta mapa jest jedynym miejscem tej decyzji —
// czytają ją i backend (dobór ceny do checkoutu Stripe), i panele.

/** Nazwy krajów (PL/EN, jak trafiają do `organizations.country`) → ISO 3166-1 alpha-2. */
const KRAJ_NAZWA_NA_KOD: Record<string, string> = {
  polska: 'PL',
  poland: 'PL',
  niemcy: 'DE',
  germany: 'DE',
  czechy: 'CZ',
  'czech republic': 'CZ',
  czechia: 'CZ',
  słowacja: 'SK',
  slovakia: 'SK',
  litwa: 'LT',
  lithuania: 'LT',
  'united kingdom': 'GB',
  'wielka brytania': 'GB',
  francja: 'FR',
  france: 'FR',
  hiszpania: 'ES',
  spain: 'ES',
  włochy: 'IT',
  italy: 'IT',
  holandia: 'NL',
  niderlandy: 'NL',
  netherlands: 'NL',
  belgia: 'BE',
  belgium: 'BE',
  austria: 'AT',
  irlandia: 'IE',
  ireland: 'IE',
  portugalia: 'PT',
  portugal: 'PT',
  'stany zjednoczone': 'US',
  'united states': 'US',
  usa: 'US',
};

/**
 * Normalizacja kraju do ISO 3166-1 alpha-2: przyjmuje kod ('PL'), polską lub
 * angielską nazwę ('Polska', 'Poland'). Nierozpoznane wejście → null (nie
 * zgadujemy — o skutkach decyduje wołający).
 */
export function kodKraju(country: string | null | undefined): string | null {
  if (!country) return null;
  const trimmed = country.trim();
  if (trimmed.length === 0) return null;
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  return KRAJ_NAZWA_NA_KOD[trimmed.toLowerCase()] ?? null;
}

/** Kraje strefy euro (stan 2026; 'EL' to unijny prefiks VAT Grecji obok ISO 'GR'). */
const STREFA_EURO = new Set<string>([
  'AT',
  'BE',
  'HR',
  'CY',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'EL',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PT',
  'SK',
  'SI',
  'ES',
]);

/**
 * Waluta rozliczeniowa dla kraju klienta. Brak kraju traktujemy jak Polskę
 * (platforma jest polska, a `organizations.country` bywa puste) — dokładnie
 * tak samo rozstrzyga to `vatPolicy.resolveVatTreatment`.
 */
export function walutaDlaKraju(country: string | null | undefined): CurrencyCode {
  const kod = kodKraju(country);
  if (kod === null || kod === 'PL') return 'PLN';
  if (kod === 'US') return 'USD';
  if (STREFA_EURO.has(kod)) return 'EUR';
  return 'EUR';
}

/**
 * Brutto jednej sztuki W GROSZACH z ceny netto (jednostki główne, np. zł)
 * i stawki VAT w procentach.
 *
 * Jedna funkcja dla obu stron kasy: ceny zakładanej w Stripe
 * (`services/neurosense-core/src/services/kasa/cenaStripe.ts`) i metki brutto
 * w panelu (`apps/universe-shell/src/lib/cenaOprogramowania.ts`,
 * `marketplaceDevices.ts`). Do 02.10.2026 każda strona liczyła po swojemu
 * w zmiennym przecinku (`netto × 1,23`), a iloczyn bywa niedokładny jeszcze
 * przed zaokrągleniem: 29,50 zł netto dawało w panelu 36,28 zł, a w Checkoucie
 * 36,29 zł (1 375 rozjazdów na zakresie 0,01–20 000 zł, wszystkie przy ,50).
 *
 * Arytmetyka całkowita: netto → grosze, mnożenie przez `100 + stawka` jest
 * dokładne, a połówka (2 950 × 123 / 100 = 3 628,5) jest w IEEE-754
 * reprezentowalna dokładnie i zaokrągla się w górę (half-up).
 */
export function bruttoWGroszach(netto: number, stawkaVat: number): number {
  const nettoGrosze = Math.round(netto * 100);
  return Math.round((nettoGrosze * (100 + stawkaVat)) / 100);
}

/**
 * Common tax rates in Poland
 */
export const POLISH_TAX_RATES = {
  STANDARD: 23,
  REDUCED: 8,
  SUPER_REDUCED: 5,
  ZERO: 0,
} as const;
